<template>
  <transition name="mask-fade">
    <div
        v-if="showMask"
        class="h5-fullscreen-mask"
        :style="maskStyle"
        @click="onMaskClick"
        @touchmove.passive.prevent="preventTouchMove"
        role="dialog"
        aria-modal="true"
    >
      <div
          class="h5-fullscreen-mask__content"
          :style="{ pointerEvents: contentClickThrough ? 'none' : 'auto' }"
          @click.stop
      >
        <!-- 默认插槽，用来放内容 -->
        <slot>
          <!-- 默认中间加载样式（可被 slot 覆盖） -->
          <div class="h5-mask-default-content">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>
        </slot>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';

const props = defineProps({
  showMask: { type: Boolean, default: true },
  modelValue: { type: Boolean, default: false }, // v-model:visible
  background: { type: String, default: 'rgba(0,0,0,0.6)' },
  zIndex: { type: Number, default: 1000 },
  closable: { type: Boolean, default: true }, // 点击遮罩是否关闭
  lockScroll: { type: Boolean, default: true }, // 是否锁定 body 滚动
  contentClickThrough: { type: Boolean, default: false }, // 内容是否可以穿透（pointer-events: none）
});

const emit = defineEmits(['update:modelValue', 'open', 'close']);

const visibleLocal = ref(props.modelValue);

watch(() => props.modelValue, (v) => {
  visibleLocal.value = v;
});

watch(visibleLocal, (v) => {
  emit('update:modelValue', v);
  if (v) emit('open');
  else emit('close');
  toggleBodyLock(v && props.lockScroll);
});

onMounted(() => {
  // 如果初始为打开，锁滚动
  if (visibleLocal.value && props.lockScroll) toggleBodyLock(true);
});

onBeforeUnmount(() => {
  // 确保解锁
  toggleBodyLock(false);
});

/** 样式相关 */
const maskStyle = computed(() => ({
  background: props.background,
  zIndex: props.zIndex,
}));

/** 点击遮罩 */
function onMaskClick(e: MouseEvent) {
  if (!props.closable) return;
  // 若点击的是 mask 背景（外层 div），则关闭
  // content 区被 stopPropagation，故到达这里一定是背景点击
  visibleLocal.value = false;
}

/** 禁用 touchmove 的目的：防止 body 在弹层打开时滑动（移动端） */
function preventTouchMove(e: TouchEvent) {
  // 如果想允许内部滚动的场景更复杂时，可在 slot 内部用 .stopPropagation 或设置样式 overflow:auto
  // 这里简单阻止默认，避免穿透滚动
  // 注意：@touchmove.passive.prevent 已经 preventDefault（需浏览器支持）
}

/** body 滚动锁定实现（简单、兼容移动端） */
let scrollTopBeforeLock = 0;
function toggleBodyLock(lock: boolean) {
  const body = document.body;
  if (!body) return;
  if (lock) {
    // 记录当前位置并固定 body
    scrollTopBeforeLock = window.scrollY || document.documentElement.scrollTop || 0;
    body.style.position = 'fixed';
    body.style.top = `-${scrollTopBeforeLock}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  } else {
    // 恢复
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, scrollTopBeforeLock);
    scrollTopBeforeLock = 0;
  }
}

/** 对外编程式接口（如果需要） */
function open() {
  visibleLocal.value = true;
}
function close() {
  visibleLocal.value = false;
}
// 暴露给模板/外部（可选）
defineExpose({ open, close });
</script>

<style scoped lang="scss">
.h5-fullscreen-mask {
  position: fixed;
  inset: 0; /* top:0; right:0; bottom:0; left:0; */
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  /* 防止 iOS 点击高亮 */
}

/* 内容容器，默认居中 */
.h5-fullscreen-mask__content {
  max-width: 92%;
  max-height: 92%;
  box-sizing: border-box;
  /* 如果你想让内容有自动滚动，添加 overflow: auto; */
}

/* 简单的默认 loading 内容 */
.h5-mask-default-content {
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:8px;
  color: #fff;
  font-size: 14px;
}
.spinner {
  width:36px;
  height:36px;
  border-radius:50%;
  border:3px solid rgba(255,255,255,0.2);
  border-top-color: #fff;
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 淡入淡出动画 */
.mask-fade-enter-active, .mask-fade-leave-active {
  transition: opacity 240ms ease;
}
.mask-fade-enter-from, .mask-fade-leave-to {
  opacity: 0;
}
.mask-fade-enter-to, .mask-fade-leave-from {
  opacity: 1;
}

/* 移动端友好：避免文本被放大，按钮触摸优化 */
html, body {
  -webkit-text-size-adjust: 100%;
}
</style>
